import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Save, Loader2, Type } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTextsTab() {
  const [activeLang, setActiveLang] = useState("de");
  const [texts, setTexts] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/texts`);
      setTexts(data);
    } catch (e) {
      toast.error("Texte konnten nicht geladen werden");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setHero = (field, value) => {
    setTexts((prev) => ({
      ...prev,
      [activeLang]: { ...prev[activeLang], hero: { ...prev[activeLang].hero, [field]: value } },
    }));
  };
  const setArgsField = (field, value) => {
    setTexts((prev) => ({
      ...prev,
      [activeLang]: { ...prev[activeLang], args: { ...prev[activeLang].args, [field]: value } },
    }));
  };
  const setArgsItem = (idx, field, value) => {
    setTexts((prev) => {
      const items = [...prev[activeLang].args.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, [activeLang]: { ...prev[activeLang], args: { ...prev[activeLang].args, items } } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/texts`, { lang: activeLang, payload: texts[activeLang] }, {
        withCredentials: true,
      });
      toast.success(`Texte (${activeLang.toUpperCase()}) gespeichert`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (!texts) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5993A]" />
      </div>
    );
  }

  const cur = texts[activeLang];

  return (
    <div data-testid="admin-texts-tab" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="font-heading text-2xl text-white flex items-center gap-2">
          <Type className="w-6 h-6 text-[#C5993A]" />
          Texte verwalten
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#12202f] border border-[#3A4F60]/50 rounded p-1">
            {["de", "en"].map((lng) => (
              <button
                key={lng}
                data-testid={`texts-lang-${lng}`}
                onClick={() => setActiveLang(lng)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded transition-colors ${activeLang === lng ? "bg-[#C5993A] text-white" : "text-[#8899AA] hover:text-white"}`}
              >
                {lng}
              </button>
            ))}
          </div>
          <Button data-testid="texts-save-button" onClick={save} disabled={saving} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </div>

      {/* HERO */}
      <section className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-5 space-y-4">
        <h3 className="font-heading text-lg text-[#C5993A]">Hero-Sektion</h3>
        <div className="grid gap-3">
          <Field label="Kleine Auszeichnung (oberhalb der Headline)">
            <Input data-testid="hero-tag" value={cur.hero.tag || ""} onChange={(e) => setHero("tag", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white" />
          </Field>
          <Field label="Headline Zeile 1 (weiß)">
            <Input data-testid="hero-h1-1" value={cur.hero.h1_1 || ""} onChange={(e) => setHero("h1_1", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white" />
          </Field>
          <Field label="Headline Zeile 2 (gold, fett)">
            <Input data-testid="hero-h1-2" value={cur.hero.h1_2 || ""} onChange={(e) => setHero("h1_2", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white" />
          </Field>
          <Field label="Beschreibungstext (unter Headline)">
            <Textarea data-testid="hero-sub" value={cur.hero.sub || ""} onChange={(e) => setHero("sub", e.target.value)} rows={4} className="bg-[#0C1620] border-[#3A4F60]/50 text-white" />
          </Field>
        </div>
      </section>

      {/* ARGS */}
      <section className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-5 space-y-4">
        <h3 className="font-heading text-lg text-[#C5993A]">Argumente-Sektion</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Sektion-Tag">
            <Input data-testid="args-tag" value={cur.args.tag || ""} onChange={(e) => setArgsField("tag", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white" />
          </Field>
          <Field label="Sektion-Überschrift (H2)">
            <Input data-testid="args-h2" value={cur.args.h2 || ""} onChange={(e) => setArgsField("h2", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white" />
          </Field>
        </div>

        <div className="space-y-4">
          {cur.args.items.map((it, idx) => (
            <div key={idx} data-testid={`arg-item-${idx + 1}`} className="bg-[#0C1620]/60 border border-[#3A4F60]/30 rounded p-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-heading text-2xl text-[#C5993A]/50 leading-none">0{idx + 1}</span>
                <span className="text-xs uppercase tracking-wider text-[#8899AA]">Argument {idx + 1}</span>
              </div>
              <Field label="Titel">
                <Input data-testid={`arg-${idx + 1}-title`} value={it.title || ""} onChange={(e) => setArgsItem(idx, "title", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white" />
              </Field>
              <Field label={'Highlight (z. B. „+1.400 %")'}>
                <Input data-testid={`arg-${idx + 1}-hl`} value={it.hl || ""} onChange={(e) => setArgsItem(idx, "hl", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white" />
              </Field>
              <Field label="Text">
                <Textarea data-testid={`arg-${idx + 1}-text`} value={it.text || ""} onChange={(e) => setArgsItem(idx, "text", e.target.value)} rows={4} className="bg-[#0C1620] border-[#3A4F60]/50 text-white" />
              </Field>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#8899AA] mb-1">{label}</span>
      {children}
    </label>
  );
}
